"use client";

import { useEffect, useState } from "react";
import { ApiError, api, loadTokens } from "@/lib/api";
import { useRouter } from "next/navigation";
import { useTranslation } from "react-i18next";
import { useKycGateRedirect } from "@/lib/useKycGateRedirect";

/**
 * EPIC-029 STORY-1702/1709/1710 — shared Transak Hosted Widget panel.
 *
 * Used by /deposit (product=BUY) and /withdraw (product=SELL). The
 * backend resolves the user's context (email, country, currency) and
 * returns a signed widget URL; we render it in a pinned-aspect
 * iframe. Sandbox mode shows a banner so the user knows no real
 * money moves; production mode is gated by `regions.real_money_enabled`
 * (admin flips per-region per launch order MX → CA → IN → BR).
 *
 * Errors surface inline. The most common is "real-money flow not
 * yet enabled for your region" when production env hits a region
 * that hasn't flipped real_money_enabled yet.
 */
export function TransakWidgetPanel({
  product,
  title,
  intro,
}: {
  product: "deposit" | "withdraw";
  title: string;
  intro: string;
}) {
  const { t } = useTranslation();
  const router = useRouter();
  const kycRedirect = useKycGateRedirect();
  const [authChecked, setAuthChecked] = useState(false);
  const [state, setState] = useState<
    | { kind: "loading" }
    | {
        kind: "ready";
        widgetUrl: string;
        environment: "sandbox" | "production";
        testMode: boolean;
      }
    | { kind: "error"; message: string }
  >({ kind: "loading" });

  useEffect(() => {
    if (!loadTokens()) {
      router.replace(`/login?next=/${product}`);
      return;
    }
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setAuthChecked(true);
  }, [product, router]);

  useEffect(() => {
    if (!authChecked) return;
    let cancelled = false;
    void (product === "deposit"
      ? api.initTransakDeposit()
      : api.initTransakWithdrawal()
    )
      .then((res) => {
        if (cancelled) return;
        // STORY-1718 Part 2 type-prep widened widgetUrl to nullable
        // for direct-PSP adapters (AsaasAdapter PIX flow). The
        // TransakWidgetPanel only renders Transak responses today
        // — BR users go through the AsaasDepositPanel surface
        // instead — so a null widgetUrl here is an unexpected
        // adapter routing error. Surface it as the same kind of
        // inline error the rest of the panel uses.
        if (res.widgetUrl === null) {
          setState({
            kind: "error",
            message:
              res.adapterId === "asaas"
                ? t("transakPanel.errAsaasReload")
                : t("transakPanel.errAdapterNoUrl", {
                    adapter: res.adapterId,
                  }),
          });
          return;
        }
        setState({
          kind: "ready",
          widgetUrl: res.widgetUrl,
          environment: res.environment,
          testMode: res.testMode,
        });
      })
      .catch((err) => {
        if (cancelled) return;
        // EPIC-050 STORY-2201 — intercept Tier{N}KycRequired and
        // redirect to /kyc/tier{N} instead of rendering the error
        // inline. The user lands back here via ?next= once they
        // promote their tier.
        if (kycRedirect(err)) return;
        const message =
          err instanceof ApiError
            ? err.message
            : err instanceof Error
              ? err.message
              : t("transakPanel.errInitFailed");
        setState({ kind: "error", message });
      });
    return () => {
      cancelled = true;
    };
  }, [authChecked, product]);

  if (!authChecked) return null;

  return (
    <main className="mx-auto max-w-3xl p-5">
      <h1 className="mb-2 text-[22px] font-bold tracking-tight">{title}</h1>
      <p className="mb-4 text-[12px] text-[var(--color-text3)]">{intro}</p>

      {state.kind === "loading" && (
        <p className="rounded-md border border-dashed border-[var(--color-border)] p-6 text-center text-[12px] text-[var(--color-text3)]">
          {t("transakPanel.initializing")}
        </p>
      )}

      {state.kind === "error" && (
        <div
          role="alert"
          className="rounded-md border border-dashed p-6 text-center text-[12px]"
          style={{
            borderColor: "var(--color-no)",
            color: "var(--color-no)",
          }}
        >
          {state.message}
        </div>
      )}

      {state.kind === "ready" && (
        <>
          {state.testMode && (
            <div
              className="mb-3 rounded-md border px-3 py-2 text-[11px]"
              style={{
                borderColor: "var(--color-brand)",
                background: "var(--color-brand-tint)",
                color: "var(--color-brand)",
              }}
            >
              <strong>{t("transakPanel.sandboxLabel")}</strong>{" "}
              {t("transakPanel.sandboxBody")}
            </div>
          )}
          <div
            className="overflow-hidden rounded-md border border-[var(--color-border)]"
            style={{ aspectRatio: "3 / 4", minHeight: 600 }}
          >
            <iframe
              src={state.widgetUrl}
              title={title}
              allow="accelerometer; autoplay; camera; gyroscope; payment; clipboard-write"
              className="h-full w-full"
              style={{ border: 0 }}
            />
          </div>
          <p className="mt-3 text-[10px] text-[var(--color-text3)]">
            {t("transakPanel.poweredByPrefix")} ·{" "}
            <a
              href="https://transak.com/privacy-policy"
              target="_blank"
              rel="noopener noreferrer"
              className="underline hover:text-[var(--color-text2)]"
            >
              {t("transakPanel.transakPrivacy")}
            </a>{" "}
            ·{" "}
            <a
              href="https://transak.com/terms-of-service"
              target="_blank"
              rel="noopener noreferrer"
              className="underline hover:text-[var(--color-text2)]"
            >
              {t("transakPanel.transakTerms")}
            </a>
          </p>
        </>
      )}
    </main>
  );
}
